import { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, User, MapPin, Euro, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { IMAGES } from '@/assets/images';
import { CashOrder, ROUTE_PATHS } from '@/lib/index';
import { Link } from 'react-router-dom';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/lib/language';

export default function Order() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<CashOrder>({
    senderName: '',
    senderPhone: '',
    senderEmail: '',
    senderAddress: '',
    amount: 0,
    currency: 'PLN',
    deliveryType: 'express',
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [orderNumber, setOrderNumber] = useState<string>('');

  const handleInputChange = (field: keyof CashOrder, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateFees = () => {
    const baseAmount = formData.amount;
    const serviceFee = formData.deliveryType === 'express' ? baseAmount * 0.03 : baseAmount * 0.02;
    const insuranceFee = baseAmount * 0.10; // 10%
    const totalFees = serviceFee + insuranceFee;
    
    return {
      serviceFee,
      insuranceFee,
      totalAmount: baseAmount + totalFees
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const newOrderNumber = `EH${Date.now().toString().slice(-6)}`.toUpperCase();
      const fees = calculateFees();
      
      console.log('Creating order with number:', newOrderNumber);
      console.log('Order data:', { newOrderNumber, fees, formData });
      
      // Walidacja danych przed zapisem
      if (!formData.senderName || !formData.senderPhone || !formData.senderEmail || !formData.senderAddress) {
        console.error('Missing required fields');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }
      
      if (!formData.amount || formData.amount <= 0) {
        console.error('Invalid amount');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }
      
      // Zapis w bazie danych
      const { data: insertData, error: dbError } = await supabase
        .from('cash_orders_20260303')
        .insert({
          order_number: newOrderNumber,
          sender_name: formData.senderName,
          sender_phone: formData.senderPhone,
          sender_email: formData.senderEmail,
          sender_address: formData.senderAddress,
          amount: formData.amount,
          currency: formData.currency,
          delivery_type: formData.deliveryType,
          service_fee: fees.serviceFee,
          insurance_fee: fees.insuranceFee,
          total_amount: fees.totalAmount,
          notes: formData.notes || '',
          status: 'nieopłacone'
        });
      
      console.log('Insert result:', { insertData, dbError });
      
      if (dbError) {
        console.error('Database error details:', {
          message: dbError.message,
          details: dbError.details,
          hint: dbError.hint,
          code: dbError.code
        });
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }
      
      console.log('Order saved successfully with number:', newOrderNumber);
      
      // Wysłanie powiadomienia Telegram z debugowaniem
      console.log('Sending Telegram notification with data:', {
        type: 'order',
        data: {
          ...formData,
          order_number: newOrderNumber,
          orderNumber: newOrderNumber, // Dodajemy też tę wersję
          ...fees
        }
      });
      
      const { error: telegramError } = await supabase.functions.invoke('telegram_notifications_20260304_final', {
        body: {
          type: 'order',
          data: {
            ...formData,
            order_number: newOrderNumber,
            orderNumber: newOrderNumber, // Dodajemy też tę wersję
            ...fees
          }
        }
      });
      
      if (telegramError) {
        console.error('Telegram error details:', {
          message: telegramError.message,
          details: telegramError.details
        });
        // Nie przerywamy procesu - Telegram to tylko powiadomienie
      }

      // Wysłanie emaila z potwierdzeniem
      const { error: emailError } = await supabase.functions.invoke('send_order_email_20260303', {
        body: {
          email: formData.senderEmail,
          orderNumber: newOrderNumber,
          orderData: {
            ...formData,
            ...fees
          }
        }
      });

      if (emailError) {
        console.error('Email error details:', {
          message: emailError.message,
          details: emailError.details
        });
        // Nie przerywamy procesu - email to tylko potwierdzenie
      }

      setOrderNumber(newOrderNumber);
      setSubmitStatus('success');
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fees = calculateFees();

  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={springPresets.gentle}
            className="max-w-md mx-auto"
          >
            <Card>
              <CardContent className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2">{t('order.orderPlaced')}</h2>
                <p className="text-muted-foreground mb-4">
                  {t('order.orderAccepted')}
                </p>
                <div className="bg-muted p-4 rounded-lg mb-6">
                  <p className="text-sm text-muted-foreground mb-1">{t('order.orderNumber')}:</p>
                  <p className="text-xl font-mono font-bold">{orderNumber}</p>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('order.contactInfo')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button asChild>
                      <Link to={`${ROUTE_PATHS.TRACKING}?order=${orderNumber}`}>
                        <Package className="w-4 h-4 mr-2" />
                        {t('order.trackOrder')}
                      </Link>
                    </Button>
                    <Button variant="outline" onClick={() => setSubmitStatus('idle')}>
                      {t('order.placeAnother')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="text-center mt-6 text-sm text-muted-foreground">
              {t('order.contactUs')}: +48 22 263 91 91 (PL) | +41 415 331 634 (CH)
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Package className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-bold">{t('order.title')}</h1>
            </div>
            <p className="text-muted-foreground">
              {t('order.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Szczegóły zamówienia */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  {t('order.orderDetails')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Dane osobowe */}
                <div className="bg-muted/30 p-6 rounded-lg space-y-4">
                  <h3 className="font-semibold text-lg mb-4">{t('order.personalData')}</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        {t('order.senderName')} *
                      </label>
                      <Input
                        type="text"
                        value={formData.senderName}
                        onChange={(e) => handleInputChange('senderName', e.target.value)}
                        placeholder={t('common.name')}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        {t('order.senderPhone')} *
                      </label>
                      <Input
                        type="tel"
                        value={formData.senderPhone}
                        onChange={(e) => handleInputChange('senderPhone', e.target.value)}
                        placeholder="+48 22 263 91 91"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t('order.senderEmail')} *
                    </label>
                    <Input
                      type="email"
                      value={formData.senderEmail}
                      onChange={(e) => handleInputChange('senderEmail', e.target.value)}
                      placeholder="email@example.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t('order.senderAddress')} *
                    </label>
                    <Textarea
                      value={formData.senderAddress}
                      onChange={(e) => handleInputChange('senderAddress', e.target.value)}
                      placeholder="Via Vite, 6, Stabio, 6883 Novazzano, Szwajcaria"
                      required
                      rows={2}
                    />
                  </div>
                </div>

                {/* Szczegóły finansowe */}
                <div className="bg-blue-50/50 p-6 rounded-lg space-y-4">
                  <h3 className="font-semibold text-lg mb-4">{t('order.financialDetails')}</h3>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">
                        {t('order.transferAmount')} *
                      </label>
                      <Input
                        type="number"
                        min="1"
                        max="800000"
                        step="0.01"
                        value={formData.amount || ''}
                        onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('order.maxAmount')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        {t('common.currency')} *
                      </label>
                      <Select value={formData.currency} onValueChange={(value) => handleInputChange('currency', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PLN">PLN (zł)</SelectItem>
                          <SelectItem value="USD">USD ($)</SelectItem>
                          <SelectItem value="EUR">EUR (€)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t('order.deliveryType')} *
                    </label>
                    <Select value={formData.deliveryType} onValueChange={(value) => handleInputChange('deliveryType', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="express">{t('order.express')}</SelectItem>
                        <SelectItem value="standard">{t('order.standard')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t('common.notes')}
                    </label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      placeholder={t('common.notes')}
                      rows={3}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Podsumowanie kosztów */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Euro className="w-5 h-5" />
                  {t('order.totalCost')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span>{t('order.transferAmount')}:</span>
                    <span className="font-mono">{formData.amount.toFixed(2)} {formData.currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('order.serviceFee')} ({formData.deliveryType === 'express' ? '3%' : '2%'}):</span>
                    <span className="font-mono">{fees.serviceFee.toFixed(2)} {formData.currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('order.insurance')}:</span>
                    <span className="font-mono">{fees.insuranceFee.toFixed(2)} {formData.currency}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between font-semibold text-lg">
                    <span>{t('order.totalCost')}:</span>
                    <span className="font-mono">{fees.totalAmount.toFixed(2)} {formData.currency}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center">
              <Button 
                type="submit" 
                size="lg" 
                disabled={isSubmitting || !formData.senderName || !formData.amount}
                className="px-8"
              >
                {isSubmitting ? t('order.submitting') : t('order.submitButton')}
              </Button>
            </div>
          </form>

          {submitStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6"
            >
              <Card className="border-destructive">
                <CardContent className="text-center py-6">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
                  <h3 className="text-lg font-semibold mb-2">{t('common.error')}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t('contact.form.error')}
                  </p>
                  <Button variant="outline" onClick={() => setSubmitStatus('idle')}>
                    {t('common.back')}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}